/**
 * The musical clock.
 *
 * A demo record is not a buffer being played — it is an arrangement rendered
 * ahead of a moving position. This maps that position to and from
 * `AudioContext.currentTime`, so seeking is a re-anchor rather than a restart
 * and the pitch fader can change rate mid-bar without a glitch.
 */

export class MusicalClock {
  rate = 1;
  running = false;

  #pos = 0;
  #posAtStart = 0;
  #timeAtStart = 0;

  /** Musical position in seconds, given the context's clock. */
  position(now: number): number {
    if (!this.running) return this.#pos;
    return this.#posAtStart + (now - this.#timeAtStart) * this.rate;
  }

  /** Start running from the frozen position. */
  start(now: number): void {
    this.running = true;
    this.#anchor(this.#pos, now);
  }

  /** Freeze at the current position. */
  stop(now: number): void {
    this.#pos = this.position(now);
    this.running = false;
  }

  seek(pos: number, now: number): void {
    this.#pos = pos;
    if (this.running) this.#anchor(pos, now);
  }

  setRate(rate: number, now: number): void {
    const pos = this.position(now);
    this.rate = rate;
    if (this.running) this.#anchor(pos, now);
  }

  /** AudioContext time at which a musical position will be heard. */
  timeOf(pos: number): number {
    return this.#timeAtStart + (pos - this.#posAtStart) / this.rate;
  }

  #anchor(pos: number, now: number): void {
    this.#posAtStart = pos;
    this.#timeAtStart = now;
  }
}
